// src/shared/changelog/components/EntryCard.tsx
import React from "react";
import StatusBadge from "@ui-components/Tag/StatusBadge";
import GenericInlineAlert from "@ui-components/Alert/GenericInlineAlert";
import { CHANGELOG_PRODUCT_META } from "../products";
import { CHANGE_CATEGORY_LABEL, ChangelogEntry } from "../types";
import { CATEGORY_VARIANT } from "./categoryVariant";

interface EntryCardProps {
  entry: ChangelogEntry;
  /** Highlights the entry when deep-linked via the URL hash. */
  isTargeted?: boolean;
}

/**
 * One changelog entry: its category badge, title, description, the products it
 * belongs to, and — when the change demands something of the customer — an
 * action-required callout.
 *
 * `isHighlight` marks the month's headline change with a brand rail, so a month
 * with a dozen entries still reads at a glance. `isTargeted` is transient
 * (someone opened a `#entryKey` link) and wins over the rail.
 */
const EntryCard: React.FC<EntryCardProps> = ({ entry, isTargeted = false }) => {
  const {
    entryKey,
    products,
    category,
    title,
    description,
    actionRequired,
    isHighlight = false,
  } = entry;

  const accent = isTargeted
    ? "border-semantic-border-brand-primary bg-semantic-bg-brand-secondary"
    : isHighlight
      ? "border-l-2 border-l-semantic-bg-brand-bold border-semantic-border-secondary bg-semantic-bg-primary"
      : "border-semantic-border-secondary bg-semantic-bg-primary";

  return (
    <li id={entryKey} className={`scroll-mt-24 rounded-lg border p-4 ${accent}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
        <StatusBadge
          variant={CATEGORY_VARIANT[category]}
          withIcon={false}
          label={CHANGE_CATEGORY_LABEL[category]}
        />
        <h3
          className={`text-sm text-semantic-text-primary ${
            isHighlight ? "font-semibold" : "font-medium"
          }`}
        >
          {title}
        </h3>
        <div className="ml-auto flex flex-wrap gap-1.5">
          {products.map((product) => (
            <StatusBadge
              key={product}
              variant="neutral"
              withIcon={false}
              label={CHANGELOG_PRODUCT_META[product].label}
            />
          ))}
        </div>
      </div>

      <p className="mt-1.5 text-xs text-semantic-text-secondary">
        {description}
      </p>

      {actionRequired && (
        <GenericInlineAlert
          variant="warning"
          title="Action required"
          className="mt-3"
        >
          {actionRequired}
        </GenericInlineAlert>
      )}
    </li>
  );
};

export default EntryCard;
